import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Tag, Sparkles, Wand2, Loader2, Plus, ArrowLeft, ExternalLink, Download, Share2,
  Send, Wallet, Ticket, CheckCircle2, Megaphone, Palette, Link as LinkIcon, BarChart3,
  ShieldCheck, Copy, Layers, Upload, Trash2, RefreshCw, DollarSign, CreditCard, KeyRound,
  Users, Trophy, TrendingUp, Zap, Info, AlertTriangle, MapPin, ScanLine,
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { sr } from "../../lib/sr";

const GOALS = [
  ["visits", "Store visits"], ["leads", "Leads"], ["purchases", "Purchases"],
  ["loyalty", "Loyalty / repeat"], ["conquesting", "Competitor conquesting"],
];
const DISTRIB = ["Display", "Social", "Email", "SMS", "RadioLife", "Loyalty"];

// Trigger a browser download from an authed blob response.
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

const StatusBadge = ({ s }) => {
  const map = { PUBLISHED: ["#E7F6EC", "#067647"], DRAFT: ["#EEF1F0", "#5B6B85"], PAUSED: ["#FCF1DC", "#9A7412"] };
  const [bg, c] = map[s] || map.DRAFT;
  return <span className="sr-badge" style={{ background: bg, color: c }}>{s || "DRAFT"}</span>;
};

const WalletChips = ({ providers }) => (
  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
    {(providers || []).map((p) => (
      <span key={p.key} className="sr-badge sr-badge-gray" title={p.demo ? "Demo mode — add credentials to go live on-device" : "Live"}>
        <Wallet size={11} /> {p.name}{p.demo ? " · demo" : " · live"}
      </span>
    ))}
  </div>
);

// Email delivery status (from Mailgun webhooks) shown in the send log.
const DELIVERY_STYLES = {
  delivered: ["#E7F6EC", "#067647"],
  opened: ["#E7F0FF", "#1D4ED8"],
  clicked: ["#E7F0FF", "#1D4ED8"],
  bounced: ["#FDECEC", "#B42318"],
  deferred: ["#FCF1DC", "#9A7412"],
  complained: ["#FDECEC", "#B42318"],
};
function deliveryBadge(s) {
  if ((s.method || "").toLowerCase() !== "email") return <span className="sr-muted" style={{ fontSize: 12 }}>—</span>;
  const st = s.delivery_status || s.delivery_event;
  if (!st) return <span className="sr-muted" style={{ fontSize: 12 }}>pending…</span>;
  const [bg, c] = DELIVERY_STYLES[st] || ["#EEF1F0", "#5B6B85"];
  return <span className="sr-badge" style={{ background: bg, color: c }} title={s.delivery_reason || st}>{st}</span>;
}

const DELIVERY_ORDER = [
  ["delivered", "#067647"], ["opened", "#1D4ED8"], ["clicked", "#2563EB"],
  ["bounced", "#B42318"], ["deferred", "#9A7412"], ["complained", "#9333EA"], ["pending", "#94A3B8"],
];
function DeliveryInsights({ sends }) {
  const [supp, setSupp] = useState(null);
  useEffect(() => { sr.offerStudio.suppressions().then(setSupp).catch(() => setSupp(null)); }, []);
  const emails = (sends || []).filter((s) => (s.method || "").toLowerCase() === "email");
  if (emails.length === 0) return null;
  const counts = {};
  emails.forEach((s) => { const st = s.delivery_status || s.delivery_event || "pending"; counts[st] = (counts[st] || 0) + 1; });
  const total = emails.length;
  const segs = DELIVERY_ORDER.filter(([k]) => counts[k]);
  return (
    <div style={{ marginBottom: 12 }} data-testid="delivery-insights">
      <div className="sr-eyebrow" style={{ marginBottom: 6 }}>Email delivery ({total})</div>
      <div style={{ display: "flex", height: 12, borderRadius: 999, overflow: "hidden", background: "#EEF1F5" }}>
        {segs.map(([k, c]) => <div key={k} title={`${k}: ${counts[k]}`} style={{ width: `${(counts[k] / total) * 100}%`, background: c }} />)}
      </div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 8 }}>
        {segs.map(([k, c]) => <span key={k} style={{ fontSize: 12, color: "var(--muted)" }}><span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: c, marginRight: 5 }} />{k} {counts[k]}</span>)}
      </div>
      {supp && supp.total > 0 && <p className="sr-muted" style={{ fontSize: 11, marginTop: 8 }} data-testid="suppression-count">{supp.emails.length} email{supp.emails.length !== 1 ? "s" : ""} auto-suppressed from future blasts (hard bounces / complaints).</p>}
    </div>
  );
}

// ---------------- Counter Mode (Staff PIN) ----------------
const EXPIRY_OPTIONS = [["", "Never expires"], ["1", "1 day"], ["7", "7 days"], ["30", "30 days"]];

function CounterModeCard() {
  const [status, setStatus] = useState(null);
  const [pin, setPin] = useState("");
  const [expiry, setExpiry] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [freshPin, setFreshPin] = useState("");
  const load = () => sr.offerStudio.staffPin().then(setStatus).catch(() => setStatus(null));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!/^\d{4,6}$/.test(pin)) { toast.error("PIN must be 4–6 digits."); return; }
    setBusy("save");
    try { const r = await sr.offerStudio.setStaffPin(pin, expiry || undefined); setStatus(r); setPin(""); setFreshPin(""); toast.success("Staff PIN saved. Share the workspace code + PIN with your counter staff."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save PIN"); }
    finally { setBusy(""); }
  };
  const rotate = async () => {
    setBusy("rotate");
    try { const r = await sr.offerStudio.rotateStaffPin(expiry || undefined); setStatus(r); setFreshPin(r.pin); toast.success("New PIN generated — share it with your staff. The old PIN no longer works."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not rotate PIN"); }
    finally { setBusy(""); }
  };
  const expireNow = async () => {
    setBusy("expire");
    try { const r = await sr.offerStudio.expireStaffPin(); setStatus({ ...status, ...r }); setFreshPin(""); toast.success("Current PIN expired. Rotate to issue a fresh one."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not expire PIN"); }
    finally { setBusy(""); }
  };
  const clear = async () => {
    setBusy("clear");
    try { const r = await sr.offerStudio.clearStaffPin(); setStatus({ ...r, workspace_code: status?.workspace_code }); setFreshPin(""); toast.success("Counter Mode PIN removed."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove PIN"); }
    finally { setBusy(""); }
  };

  const active = status?.has_pin && !status?.expired;
  return (
    <div className="sr-card" style={{ marginBottom: 16 }} data-testid="counter-mode-card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <KeyRound size={16} />
          <b style={{ color: "var(--navy)" }}>Counter Mode (Staff PIN)</b>
          {active
            ? <span className="sr-badge" style={{ background: "#E7F6EC", color: "#067647" }}><CheckCircle2 size={11} /> active</span>
            : status?.expired
              ? <span className="sr-badge" style={{ background: "#FCF1DC", color: "#9A7412" }}>expired</span>
              : <span className="sr-badge sr-badge-gray">off</span>}
        </div>
        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setOpen((v) => !v)} data-testid="counter-mode-toggle">{open ? "Close" : "Set up"}</button>
      </div>
      <p className="sr-muted" style={{ fontSize: 13, marginTop: 8 }}>Let cashiers redeem coupons on the mobile app with a shared PIN — no owner login, no admin access. They only see the Redeem screen.</p>

      {status?.has_pin && status?.workspace_code && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
          <span className="sr-muted" style={{ fontSize: 12 }}>Workspace code:</span>
          <span className="sr-badge" style={{ fontSize: 14, letterSpacing: 1 }} data-testid="counter-workspace-code">{status.workspace_code}</span>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => { navigator.clipboard?.writeText(status.workspace_code); toast.success("Copied"); }} data-testid="copy-workspace-code"><Copy size={13} /> Copy</button>
          {status.expires_at && !status.expired && <span className="sr-muted" style={{ fontSize: 11 }}>· PIN expires {new Date(status.expires_at).toLocaleDateString()}</span>}
        </div>
      )}

      {freshPin && (
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 10, background: "#E7F6EC", border: "1px solid #067647", borderRadius: 8, padding: "10px 12px" }} data-testid="fresh-pin-banner">
          <span className="sr-muted" style={{ fontSize: 12 }}>New PIN (share now):</span>
          <span style={{ fontSize: 22, fontWeight: 800, letterSpacing: 3, color: "#067647" }} data-testid="fresh-pin-value">{freshPin}</span>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => { navigator.clipboard?.writeText(freshPin); toast.success("Copied"); }}><Copy size={13} /> Copy</button>
        </div>
      )}

      {open && (
        <div style={{ marginTop: 12, borderTop: "1px solid var(--border)", paddingTop: 12 }}>
          <div className="sr-eyebrow" style={{ marginBottom: 6 }}>{status?.has_pin ? "Change PIN" : "Create a 4–6 digit PIN"}</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input className="sr-input" style={{ maxWidth: 140 }} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric" placeholder="e.g. 4821" data-testid="staff-pin-input" />
            <select className="sr-input" style={{ maxWidth: 160 }} value={expiry} onChange={(e) => setExpiry(e.target.value)} data-testid="staff-pin-expiry">
              {EXPIRY_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={save} disabled={!!busy} data-testid="save-staff-pin-btn">{busy === "save" ? <Loader2 size={14} className="sr-spin" /> : <><KeyRound size={14} /> Save PIN</>}</button>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 10 }}>
            <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={rotate} disabled={!!busy} data-testid="rotate-staff-pin-btn">{busy === "rotate" ? <Loader2 size={14} className="sr-spin" /> : <><RefreshCw size={14} /> Rotate PIN</>}</button>
            {active && <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={expireNow} disabled={!!busy} data-testid="expire-staff-pin-btn"><KeyRound size={14} /> Expire now</button>}
            {status?.has_pin && <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={clear} disabled={!!busy} data-testid="clear-staff-pin-btn"><Trash2 size={14} /> Turn off</button>}
          </div>
          <p className="sr-muted" style={{ fontSize: 11, marginTop: 8 }}>Rotate generates a brand-new PIN and instantly invalidates the old one. Expiry (optional) forces a fresh PIN after the chosen window.</p>
        </div>
      )}
    </div>
  );
}

// ---------------- Counter Activity feed ----------------
function CounterActivity() {
  const [data, setData] = useState(null);
  useEffect(() => { sr.offerStudio.counterActivity().then(setData).catch(() => setData(null)); }, []);
  if (!data || data.total === 0) return null;
  return (
    <div className="sr-card" style={{ marginBottom: 16 }} data-testid="counter-activity-card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div className="sr-eyebrow" style={{ display: "flex", gap: 6, alignItems: "center" }}><Ticket size={13} /> Counter activity</div>
        <div style={{ display: "flex", gap: 8 }}>
          <span className="sr-badge" style={{ background: "#EEF4FF", color: "#1D4ED8" }} data-testid="counter-today-count">{data.today_counter} counter today</span>
          <span className="sr-badge sr-badge-gray">{data.today_total} total today</span>
        </div>
      </div>
      <div style={{ maxHeight: 240, overflow: "auto", marginTop: 10 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead><tr style={{ textAlign: "left", color: "var(--muted)" }}><th style={{ padding: 6 }}>Cashier</th><th>Offer</th><th>Code</th><th>Amount</th><th>When</th></tr></thead>
          <tbody>{data.feed.slice(0, 50).map((r) => (
            <tr key={r.id} style={{ borderTop: "1px solid var(--border)" }} data-testid="counter-activity-row">
              <td style={{ padding: 6 }}><span style={{ fontWeight: 700 }}>{r.cashier}</span>{r.channel === "counter" && <span className="sr-badge" style={{ background: "#EEF4FF", color: "#1D4ED8", marginLeft: 6 }}>counter</span>}</td>
              <td>{r.offer}</td>
              <td className="sr-muted">{r.code}</td>
              <td>{r.amount != null ? `$${r.amount}` : "—"}</td>
              <td className="sr-muted">{r.created_at ? new Date(r.created_at).toLocaleString() : ""}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------- Offers list ----------------
function LandingReviewCard() {
  const [profile, setProfile] = useState(null);
  const [sel, setSel] = useState(null);
  useEffect(() => { sr.profileGet().then((r) => { const p = r.profile || {}; setProfile(p); setSel(p.featured_review_index ?? null); }).catch(() => {}); }, []);
  const revs = (profile?.reviews || []).filter((r) => r.text);
  if (!profile || profile.rating == null || revs.length === 0) return null;
  const choose = async (i) => {
    setSel(i);
    try { await sr.setFeaturedReview(i); toast.success("Landing page review updated"); }
    catch { toast.error("Couldn't save your pick"); }
  };
  return (
    <div className="sr-card" style={{ marginBottom: 16 }} data-testid="landing-review-card">
      <div className="sr-eyebrow">Review shown on your public offer page</div>
      <p className="sr-muted" style={{ fontSize: 12, margin: "4px 0 10px" }}>Pick which Google review appears on your hosted landing pages (and your dashboard trust badge). {profile.rating}★ · {profile.rating_count || 0} reviews.</p>
      <div style={{ display: "grid", gap: 8 }}>
        {revs.map((r, i) => (
          <button key={i} onClick={() => choose(i)} data-testid={`landing-review-option-${i}`}
            style={{ textAlign: "left", background: i === sel ? "var(--blue-light)" : "#fff", border: `1px solid ${i === sel ? "var(--blue)" : "var(--border)"}`, borderRadius: 8, padding: "8px 10px", cursor: "pointer", fontSize: 13, color: "var(--navy)" }}>
            <b>{r.rating ? `${r.rating}★ ` : ""}</b>{String(r.text).slice(0, 150)}{String(r.text).length > 150 ? "…" : ""}
          </button>
        ))}
      </div>
    </div>
  );
}

export function OffersList() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const load = () => sr.offerStudio.list().then(setData).catch(() => setData({ offers: [] }));
  useEffect(() => { load(); }, []);
  return (
    <div data-testid="offer-studio-page">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <span className="sr-badge" style={{ marginBottom: 8 }}><Tag size={12} /> Coupon & Wallet Offer Studio</span>
          <h1 style={{ fontSize: 30 }}>Offer Studio</h1>
          <p className="sr-muted" style={{ marginTop: 6 }}>Design AI coupon offers, hosted landing pages, display banners and Apple/Google Wallet passes — then track redemptions.</p>
          {data && <div style={{ marginTop: 10 }}><WalletChips providers={data.wallet_providers} /></div>}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => nav("/app/offers/templates")} data-testid="offers-templates-btn"><Palette size={14} /> Templates</button>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => nav("/app/offers/matchback")} data-testid="offers-matchback-btn"><DollarSign size={14} /> Revenue matchback</button>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => nav("/app/wallet-settings")} data-testid="wallet-settings-btn"><Wallet size={14} /> Wallet settings</button>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => nav("/app/wallet-passes")} data-testid="wallet-passes-btn"><Wallet size={14} /> Wallet passes</button>
          <button className="sr-btn sr-btn-gold sr-btn-sm" onClick={() => nav("/app/offers/redeem")} data-testid="scan-redeem-btn"><ScanLine size={14} /> Scan &amp; Redeem</button>
          <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => nav("/app/offers/new")} data-testid="design-with-ai-btn"><Wand2 size={14} /> Design with AI</button>
        </div>
      </div>

      {!data && <div className="sr-card"><Loader2 className="sr-spin" /></div>}
      <CounterModeCard />
      <CounterActivity />
      <LandingReviewCard />
      {data && data.offers.length === 0 && (
        <div className="sr-card" data-testid="offers-empty">
          <p className="sr-muted">No offers yet. Design your first AI coupon offer — headline, copy, fine print, landing page and wallet passes in one flow.</p>
          <button className="sr-btn sr-btn-primary" style={{ marginTop: 12 }} onClick={() => nav("/app/offers/new")}><Wand2 size={15} /> Design with AI</button>
        </div>
      )}
      <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 14 }}>
        {(data?.offers || []).map((o) => (
          <div key={o.id} className="sr-card" data-testid={`offer-card-${o.id}`} style={{ cursor: "pointer" }} onClick={() => nav(`/app/offers/${o.id}`)}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <StatusBadge s={o.status} />
              <span className="sr-badge sr-badge-gray"><Ticket size={11} /> {o.redeemed || 0} redeemed</span>
            </div>
            <div style={{ color: "var(--navy)", fontWeight: 800, fontSize: 17 }}>{o.headline}</div>
            <p className="sr-muted" style={{ fontSize: 13, marginTop: 4 }}>{o.description ?? o.offer_copy}</p>
            <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
              {o.discount_value != null && <span className="sr-badge">{o.discount_type === "percent" ? `${o.discount_value}% off` : o.discount_type === "amount" ? `$${o.discount_value} off` : o.discount_type}</span>}
              <span className="sr-badge sr-badge-gray">{((o.redemption_mode || o.code_mode) === "reusable" || o.code_mode === "REUSABLE") ? "Reusable code" : "One-time code"}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------- Design with AI (also campaign attach) ----------------
export function OfferNew() {
  const nav = useNavigate();
  const params = useParams();
  const loc = useLocation();
  const campaignId = params.id || null; // /app/campaigns/:id/offer
  const qs = new URLSearchParams(loc.search);
  const [goal, setGoal] = useState(qs.get("goal") || "visits");
  const [category, setCategory] = useState("");
  const [intent, setIntent] = useState({ time: "", margin_sensitivity: "medium" });
  const [busy, setBusy] = useState(false);
  const [aiAvailable, setAiAvailable] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [manual, setManual] = useState(false);
  const [form, setForm] = useState({
    headline: "", description: "", cta: "Get the coupon", fine_print: "", terms: "",
    discount_type: "percent", discount_value: 20, minimum_purchase: "", max_discount: "",
    start_at: "", expiration_days: 30, redemption_mode: "single-use",
    new_customer_only: false, repeat_customer_only: false,
    max_total_redemptions: "", max_redemptions_per_customer: "", daily_redemption_limit: "",
    eligible_locations: "all",
  });

  const generate = async () => {
    setBusy(true); setSuggestions([]);
    try {
      const r = await sr.offerStudio.generate({ goal, category, intent, count: 3 });
      setSuggestions(r.suggestions || []); setAiAvailable(r.ai_available);
      if (!r.ai_available) toast.message("Showing safe suggestions (AI temporarily unavailable)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate"); }
    finally { setBusy(false); }
  };

  const createFrom = async (pkg, aiGenerated = false) => {
    setBusy(true);
    try {
      // strip empty-string numerics so the backend keeps them null
      const clean = { ...pkg };
      ["minimum_purchase", "max_discount", "max_total_redemptions", "max_redemptions_per_customer", "daily_redemption_limit"].forEach((k) => {
        if (clean[k] === "" || clean[k] === null || clean[k] === undefined) delete clean[k];
        else clean[k] = Number(clean[k]);
      });
      if (!clean.start_at) delete clean.start_at;
      const body = { ...clean, goal, ai_generated: aiGenerated, banner_variants: pkg.banner_variants };
      const r = await sr.offerStudio.create(body);
      const id = r.offer.id;
      if (campaignId) {
        await sr.offerStudio.attachCampaign(id, { campaign_id: campaignId });
        toast.success("Offer created & attached to your campaign");
      } else {
        toast.success("Offer created");
      }
      nav(`/app/offers/${id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create offer"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="offer-new-page">
      <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => nav(campaignId ? "/app/campaigns" : "/app/offers")} style={{ marginBottom: 12 }}><ArrowLeft size={14} /> Back</button>
      <span className="sr-badge" style={{ marginBottom: 8 }}><Wand2 size={12} /> Design with AI → Coupon / Offer</span>
      <h1 style={{ fontSize: 28 }}>{campaignId ? "Add a coupon offer to this campaign" : "Design a coupon offer with AI"}</h1>
      <p className="sr-muted" style={{ marginTop: 6, marginBottom: 16 }}>AI suggests margin-safe offers from your business, goal and intent. Pick one or write your own.</p>

      <div className="sr-card">
        <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))" }}>
          <div className="sr-field"><label className="sr-label">Campaign goal</label>
            <select className="sr-select" value={goal} onChange={(e) => setGoal(e.target.value)} data-testid="offer-goal">
              {GOALS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></div>
          <div className="sr-field"><label className="sr-label">Category (optional)</label>
            <input className="sr-input" placeholder="e.g. Restaurant" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="offer-category" /></div>
          <div className="sr-field"><label className="sr-label">Time / day intent</label>
            <input className="sr-input" placeholder="e.g. slow weekday lunch" value={intent.time} onChange={(e) => setIntent({ ...intent, time: e.target.value })} data-testid="offer-intent-time" /></div>
          <div className="sr-field"><label className="sr-label">Margin sensitivity</label>
            <select className="sr-select" value={intent.margin_sensitivity} onChange={(e) => setIntent({ ...intent, margin_sensitivity: e.target.value })} data-testid="offer-margin">
              {["low", "medium", "high"].map((m) => <option key={m} value={m}>{m}</option>)}
            </select></div>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button className="sr-btn sr-btn-primary" onClick={generate} disabled={busy} data-testid="offer-generate-btn">
            {busy ? <><Loader2 size={16} className="sr-spin" /> Generating…</> : <><Sparkles size={16} /> Generate offers</>}
          </button>
          <button className="sr-btn sr-btn-ghost" onClick={() => setManual((m) => !m)} data-testid="offer-manual-toggle">{manual ? "Hide manual entry" : "Enter manually"}</button>
        </div>
      </div>

      {aiAvailable === false && <p className="sr-muted" style={{ fontSize: 12, marginTop: 8 }}>AI was busy — these are safe template offers you can edit.</p>}

      <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 14, marginTop: 16 }}>
        {suggestions.map((s, i) => (
          <div key={i} className="sr-card" data-testid={`offer-suggestion-${i}`} style={{ background: "var(--blue-light)" }}>
            {s.concept && <span className="sr-badge sr-badge-gray" style={{ marginBottom: 6 }}>{s.concept}</span>}
            <div style={{ color: "var(--navy)", fontWeight: 800, fontSize: 16 }}>{s.headline}</div>
            <p className="sr-muted" style={{ fontSize: 13, marginTop: 4 }}>{s.offer_copy}</p>
            <p style={{ fontSize: 11, color: "var(--muted)", marginTop: 6 }}>Fine print: {s.fine_print}</p>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
              <span className="sr-badge">{s.cta}</span>
              <span className="sr-badge sr-badge-gray">Expires {s.expiration_days}d</span>
              {s.margin_floor_ok === false && <span className="sr-badge" style={{ background: "#FDECEC", color: "#B42318" }} title="Face value above margin">margin risk</span>}
            </div>
            <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 12 }} disabled={busy} onClick={() => createFrom(s, true)} data-testid={`use-suggestion-${i}`}>
              <CheckCircle2 size={14} /> Use this offer
            </button>
          </div>
        ))}
      </div>

      {manual && (
        <div className="sr-card" style={{ marginTop: 16 }} data-testid="offer-manual-form">
          <div className="sr-eyebrow" style={{ marginBottom: 10 }}>Write your own offer</div>
          <div className="sr-field"><label className="sr-label">Headline</label>
            <input className="sr-input" value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} data-testid="manual-headline" /></div>
          <div className="sr-field"><label className="sr-label">Description</label>
            <input className="sr-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="manual-description" /></div>
          <div className="sr-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            <div className="sr-field"><label className="sr-label">Call to action</label>
              <input className="sr-input" value={form.cta} onChange={(e) => setForm({ ...form, cta: e.target.value })} data-testid="manual-cta" /></div>
            <div className="sr-field"><label className="sr-label">Fine print</label>
              <input className="sr-input" value={form.fine_print} onChange={(e) => setForm({ ...form, fine_print: e.target.value })} data-testid="manual-fine_print" /></div>
          </div>
          <div className="sr-field"><label className="sr-label">Terms (longer, shown on the coupon back)</label>
            <textarea className="sr-input" rows={2} value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} data-testid="manual-terms" /></div>

          <div className="sr-eyebrow" style={{ margin: "14px 0 6px" }}>Discount</div>
          <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))" }}>
            <div className="sr-field"><label className="sr-label">Discount type</label>
              <select className="sr-select" value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} data-testid="manual-discount-type">
                {["percent", "amount", "bogo", "freebie"].map((d) => <option key={d}>{d}</option>)}</select></div>
            <div className="sr-field"><label className="sr-label">Discount value</label>
              <input className="sr-input" type="number" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: Number(e.target.value) })} data-testid="manual-discount-value" /></div>
            <div className="sr-field"><label className="sr-label">Min. purchase ($)</label>
              <input className="sr-input" type="number" placeholder="none" value={form.minimum_purchase} onChange={(e) => setForm({ ...form, minimum_purchase: e.target.value })} data-testid="manual-min-purchase" /></div>
            <div className="sr-field"><label className="sr-label">Max discount ($/%)</label>
              <input className="sr-input" type="number" placeholder="none" value={form.max_discount} onChange={(e) => setForm({ ...form, max_discount: e.target.value })} data-testid="manual-max-discount" /></div>
          </div>

          <div className="sr-eyebrow" style={{ margin: "14px 0 6px" }}>Schedule & eligibility</div>
          <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))" }}>
            <div className="sr-field"><label className="sr-label">Starts (optional)</label>
              <input className="sr-input" type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value ? new Date(e.target.value).toISOString() : "" })} data-testid="manual-start-at" /></div>
            <div className="sr-field"><label className="sr-label">Expiration (days)</label>
              <input className="sr-input" type="number" value={form.expiration_days} onChange={(e) => setForm({ ...form, expiration_days: Number(e.target.value) })} data-testid="manual-expiration" /></div>
            <div className="sr-field"><label className="sr-label">Eligible locations</label>
              <input className="sr-input" value={typeof form.eligible_locations === "string" ? form.eligible_locations : "all"} onChange={(e) => setForm({ ...form, eligible_locations: e.target.value })} data-testid="manual-eligible-locations" /></div>
          </div>
          <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginTop: 6 }}>
            <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13 }}><input type="checkbox" checked={form.new_customer_only} onChange={(e) => setForm({ ...form, new_customer_only: e.target.checked, repeat_customer_only: e.target.checked ? false : form.repeat_customer_only })} data-testid="manual-new-only" /> New customers only</label>
            <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13 }}><input type="checkbox" checked={form.repeat_customer_only} onChange={(e) => setForm({ ...form, repeat_customer_only: e.target.checked, new_customer_only: e.target.checked ? false : form.new_customer_only })} data-testid="manual-repeat-only" /> Returning customers only</label>
          </div>

          <div className="sr-eyebrow" style={{ margin: "14px 0 6px" }}>Redemption limits</div>
          <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))" }}>
            <div className="sr-field"><label className="sr-label">Code mode</label>
              <select className="sr-select" value={form.redemption_mode} onChange={(e) => setForm({ ...form, redemption_mode: e.target.value })} data-testid="manual-redemption-mode">
                <option value="single-use">Single-use</option><option value="reusable">Reusable</option></select></div>
            <div className="sr-field"><label className="sr-label">Max total</label>
              <input className="sr-input" type="number" placeholder="unlimited" value={form.max_total_redemptions} onChange={(e) => setForm({ ...form, max_total_redemptions: e.target.value })} data-testid="manual-max-total" /></div>
            <div className="sr-field"><label className="sr-label">Per customer</label>
              <input className="sr-input" type="number" placeholder="unlimited" value={form.max_redemptions_per_customer} onChange={(e) => setForm({ ...form, max_redemptions_per_customer: e.target.value })} data-testid="manual-per-customer" /></div>
            <div className="sr-field"><label className="sr-label">Per day</label>
              <input className="sr-input" type="number" placeholder="unlimited" value={form.daily_redemption_limit} onChange={(e) => setForm({ ...form, daily_redemption_limit: e.target.value })} data-testid="manual-daily-limit" /></div>
          </div>
          <button className="sr-btn sr-btn-primary" style={{ marginTop: 12 }} disabled={busy || !form.headline} onClick={() => createFrom(form, false)} data-testid="manual-create-btn"><Plus size={15} /> Create offer</button>
        </div>
      )}
    </div>
  );
}

// ---------------- Templates gallery ----------------
const TplPreview = ({ t }) => {
  const c = t.colors || {};
  const disc = t.discount_type === "percent" ? `${t.discount_value}% OFF`
    : t.discount_type === "amount" ? `$${t.discount_value} OFF`
      : t.discount_type === "bogo" ? (t.discount_value ? `BOGO ${t.discount_value}%` : "BOGO")
        : t.discount_type === "freebie" ? "FREE" : "OFFER";
  return (
    <div style={{ background: c.bg, borderRadius: 12, overflow: "hidden", border: "1px solid rgba(0,0,0,.1)" }}>
      <div style={{ height: 5, background: c.accent }} />
      <div style={{ padding: 14 }}>
        <div style={{ color: c.accent, fontWeight: 800, fontSize: 10, letterSpacing: 1 }}>{disc}</div>
        <div style={{ color: c.fg, fontWeight: 800, fontSize: 15, lineHeight: 1.2, marginTop: 6, minHeight: 36 }}>{t.headline}</div>
        <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ color: c.fg, opacity: 0.6, fontSize: 10 }}>CODE ••••</span>
          <span style={{ background: c.accent, color: c.bg, fontWeight: 800, fontSize: 10, padding: "4px 10px", borderRadius: 999 }}>{t.cta}</span>
        </div>
      </div>
    </div>
  );
};

export function OfferTemplates() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("All");
  const [busy, setBusy] = useState("");
  useEffect(() => { sr.offerStudio.templates().then(setData).catch(() => setData({ templates: [], categories: [] })); }, []);

  const apply = async (t) => {
    setBusy(t.id);
    try { const r = await sr.offerStudio.createFromTemplate(t.id); toast.success(`“${t.name}” applied to a new offer`); nav(`/app/offers/${r.offer.id}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not use template"); } finally { setBusy(""); }
  };

  const cats = ["All", ...((data?.categories) || [])];
  const shown = (data?.templates || []).filter((t) => cat === "All" || t.category === cat);
  return (
    <div data-testid="offer-templates-page">
      <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => nav("/app/offers")} style={{ marginBottom: 12 }}><ArrowLeft size={14} /> Back</button>
      <span className="sr-badge" style={{ marginBottom: 8 }}><Palette size={12} /> Offer Templates Gallery</span>
      <h1 style={{ fontSize: 26 }}>Ready-made seasonal offers</h1>
      <p className="sr-muted" style={{ marginTop: 6, marginBottom: 14 }}>Pick a designed template — colors and starter copy included — then tap to spin up an editable offer.</p>
      {data?.recommended?.templates?.length > 0 && (
        <div style={{ background: "linear-gradient(120deg,#FFF8EE,#FDF0F5)", border: "1px solid #F0DCB8", borderRadius: 14, padding: 18, marginBottom: 18 }} data-testid="recommended-row">
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span className="sr-badge" style={{ background: "#111827", color: "#E4C066" }}><Sparkles size={12} /> Recommended right now — {data.recommended.label}</span>
          </div>
          <p className="sr-muted" style={{ fontSize: 13, marginTop: 8 }}>{data.recommended.reason}</p>
          <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 14, marginTop: 12 }}>
            {data.recommended.templates.map((t) => (
              <div key={t.id} className="sr-card" data-testid={`recommended-${t.id}`} style={{ background: "#fff" }}>
                <TplPreview t={t} />
                <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 10, width: "100%" }} disabled={busy === t.id} onClick={() => apply(t)} data-testid={`use-recommended-${t.id}`}>
                  {busy === t.id ? <Loader2 size={14} className="sr-spin" /> : <><Sparkles size={14} /> Use “{t.name}”</>}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }} data-testid="template-categories">
        {cats.map((cName) => (
          <button key={cName} onClick={() => setCat(cName)} data-testid={`tpl-cat-${cName}`}
            style={{ borderRadius: 999, padding: "6px 14px", fontWeight: 700, fontSize: 13, cursor: "pointer",
              border: cat === cName ? "1px solid var(--blue)" : "1px solid var(--border)", background: cat === cName ? "var(--blue)" : "#fff", color: cat === cName ? "#fff" : "var(--navy)" }}>{cName}</button>
        ))}
      </div>
      {!data && <div className="sr-card"><Loader2 className="sr-spin" /></div>}
      <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 16 }}>
        {shown.map((t) => (
          <div key={t.id} className="sr-card" data-testid={`template-${t.id}`}>
            <TplPreview t={t} />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
              <div style={{ fontWeight: 800, color: "var(--navy)" }}>{t.name}</div>
              <span className="sr-badge sr-badge-gray">{t.badge || t.category}</span>
            </div>
            <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 10, width: "100%" }} disabled={busy === t.id} onClick={() => apply(t)} data-testid={`use-template-${t.id}`}>
              {busy === t.id ? <Loader2 size={14} className="sr-spin" /> : <><Sparkles size={14} /> Use this template</>}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------- Offer detail (tabs) ----------------
// ---------------- Auto-send on publish ----------------
const SEGMENTS = [
  { v: "all", label: "All contacts" },
  { v: "won", label: "Past customers" },
  { v: "contacted", label: "Recently contacted" },
  { v: "new", label: "New leads" },
];

function AutoSendCard({ offer, onChange }) {
  const cfg = offer.auto_send || {};
  const [enabled, setEnabled] = useState(!!cfg.enabled);
  const [segment, setSegment] = useState(cfg.segment || "all");
  const [channels, setChannels] = useState(cfg.channels || ["sms", "email"]);
  const [aud, setAud] = useState(null);
  const [busy, setBusy] = useState(false);
  const published = offer.status === "PUBLISHED";
  const res = offer.auto_send_result;

  useEffect(() => { sr.offerStudio.audiencePreview(segment).then(setAud).catch(() => setAud(null)); }, [segment]);
  const toggleCh = (c) => setChannels((cs) => (cs.includes(c) ? cs.filter((x) => x !== c) : [...cs, c]));
  const save = async () => {
    setBusy(true);
    try { await sr.offerStudio.setAutoSend(offer.id, { enabled, segment, channels }); toast.success("Auto-send settings saved"); onChange && onChange(); }
    catch { toast.error("Could not save"); } finally { setBusy(false); }
  };

  return (
    <div style={{ marginTop: 10, padding: 14, background: "#FFF8EE", border: "1px solid #F0DCB8", borderRadius: 10 }} data-testid="auto-send-card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div className="sr-eyebrow" style={{ display: "flex", gap: 6, alignItems: "center" }}><Zap size={13} /> Auto-send on publish</div>
        <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13, fontWeight: 700 }}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="auto-send-enabled" /> On
        </label>
      </div>
      <p className="sr-muted" style={{ fontSize: 12.5, marginTop: 6 }}>When you publish this offer, we&apos;ll text/email the coupon to a segment of your contacts (opt-outs are always skipped).</p>
      <div className="sr-grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 8 }}>
        <div className="sr-field" style={{ margin: 0 }}><label className="sr-label">Send to</label>
          <select className="sr-select" value={segment} onChange={(e) => setSegment(e.target.value)} disabled={!enabled} data-testid="auto-send-segment">
            {SEGMENTS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
          </select></div>
        <div className="sr-field" style={{ margin: 0 }}><label className="sr-label">Channels</label>
          <div style={{ display: "flex", gap: 12, paddingTop: 6 }}>
            {["sms", "email"].map((c) => (
              <label key={c} style={{ display: "flex", gap: 5, alignItems: "center", fontSize: 13 }}>
                <input type="checkbox" checked={channels.includes(c)} onChange={() => toggleCh(c)} disabled={!enabled} data-testid={`auto-send-ch-${c}`} /> {c.toUpperCase()}
              </label>
            ))}
          </div></div>
      </div>
      {aud && enabled && (
        <p className="sr-muted" style={{ fontSize: 12.5, marginTop: 8 }} data-testid="auto-send-audience">
          <Users size={12} /> ~{aud.total} contacts · {aud.reachable_sms} by SMS · {aud.reachable_email} by email
        </p>
      )}
      <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 10 }} disabled={busy} onClick={save} data-testid="auto-send-save"><Send size={13} /> Save auto-send</button>
      {res && (
        <p className="sr-muted" style={{ fontSize: 12, marginTop: 8, color: "#067647" }} data-testid="auto-send-result">
          Last run: sent {res.sent} of {res.attempted} ({res.segment}){res.failed ? ` · ${res.failed} failed` : ""}
        </p>
      )}
      {enabled && !published && <p className="sr-muted" style={{ fontSize: 12, marginTop: 6 }}>Will send automatically when you publish.</p>}
    </div>
  );
}

export function PosterBranding() {
  const [b, setB] = useState(null);
  const [tagline, setTagline] = useState("");
  const [bg, setBg] = useState("#111827");
  const [accent, setAccent] = useState("#D4AF37");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const PRESETS = [
    { bg: "#0B3D2E", accent: "#E4C066" }, { bg: "#1D4ED8", accent: "#F59E0B" },
    { bg: "#7F1D1D", accent: "#FCA5A5" }, { bg: "#111827", accent: "#D4AF37" },
    { bg: "#22223B", accent: "#F2A900" }, { bg: "#0F3D3E", accent: "#8AE0C1" },
  ];
  const load = () => sr.offerStudio.posterBranding().then((d) => {
    setB(d); setTagline(d.tagline || ""); setBg(d.bg || "#111827"); setAccent(d.accent || "#D4AF37");
  }).catch(() => setB({ tagline: "", logo_url: null, bg: "#111827", accent: "#D4AF37", brand_set: false }));
  useEffect(() => { load(); }, []);
  if (!b) return null;

  const saveTagline = async () => {
    setBusy(true);
    try { const d = await sr.offerStudio.savePosterBranding({ tagline }); setB(d); toast.success("Tagline saved"); }
    catch { toast.error("Couldn't save the tagline"); } finally { setBusy(false); }
  };
  const saveColors = async (nbg, nacc) => {
    setBg(nbg); setAccent(nacc);
    setBusy(true);
    try { const d = await sr.offerStudio.savePosterBranding({ bg: nbg, accent: nacc }); setB(d); toast.success("Brand colors saved"); }
    catch { toast.error("Couldn't save the colors"); } finally { setBusy(false); }
  };
  const resetColors = async () => {
    setBusy(true);
    try { const d = await sr.offerStudio.savePosterBranding({ bg: "", accent: "" }); setB(d); setBg(d.bg); setAccent(d.accent); toast.success("Reset to default palette"); }
    catch { toast.error("Couldn't reset colors"); } finally { setBusy(false); }
  };
  const onLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try { const d = await sr.offerStudio.uploadPosterLogo(file); setB((p) => ({ ...p, logo_url: d.logo_url })); toast.success("Logo uploaded"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Couldn't upload the logo"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const removeLogo = async () => {
    setBusy(true);
    try { await sr.offerStudio.deletePosterLogo(); setB((p) => ({ ...p, logo_url: null })); toast.success("Logo removed"); }
    catch { toast.error("Couldn't remove the logo"); } finally { setBusy(false); }
  };

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border)" }} data-testid="poster-branding">
      <div className="sr-eyebrow" style={{ marginBottom: 6, display: "inline-flex", alignItems: "center", gap: 6 }}><Palette size={13} /> Poster branding</div>
      <p className="sr-muted" style={{ fontSize: 12, marginBottom: 10 }}>Add your logo, tagline and brand colors — they apply to the coupon and RadioLife posters.</p>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        {b.logo_url
          ? <img src={b.logo_url} alt="logo" data-testid="poster-logo-preview" style={{ height: 48, maxWidth: 160, objectFit: "contain", background: "#0B1220", borderRadius: 8, padding: 6, border: "1px solid var(--border)" }} />
          : <div style={{ height: 48, width: 120, borderRadius: 8, border: "1px dashed var(--border)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--muted)", fontSize: 12 }}>No logo</div>}
        <input ref={fileRef} type="file" accept="image/png,image/jpeg" onChange={onLogo} style={{ display: "none" }} data-testid="poster-logo-input" />
        <button className="sr-btn sr-btn-ghost sr-btn-sm" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="poster-logo-upload-btn"><Upload size={13} /> {b.logo_url ? "Replace logo" : "Upload logo"}</button>
        {b.logo_url && <button className="sr-btn sr-btn-ghost sr-btn-sm" disabled={busy} onClick={removeLogo} data-testid="poster-logo-remove-btn"><Trash2 size={13} /> Remove</button>}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <input className="sr-input" style={{ flex: 1 }} value={tagline} maxLength={80} onChange={(e) => setTagline(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && saveTagline()} placeholder="e.g. Family owned since 1998" data-testid="poster-tagline-input" />
        <button className="sr-btn sr-btn-primary sr-btn-sm" disabled={busy || tagline === (b.tagline || "")} onClick={saveTagline} data-testid="poster-tagline-save"><CheckCircle2 size={13} /> Save</button>
      </div>
      <div style={{ marginTop: 12 }} data-testid="poster-colors">
        <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--muted)" }}>
            Background
            <input type="color" value={bg} onChange={(e) => saveColors(e.target.value, accent)} data-testid="poster-bg-input"
              style={{ width: 40, height: 28, border: "1px solid var(--border)", borderRadius: 6, background: "none", cursor: "pointer" }} />
          </label>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--muted)" }}>
            Accent
            <input type="color" value={accent} onChange={(e) => saveColors(bg, e.target.value)} data-testid="poster-accent-input"
              style={{ width: 40, height: 28, border: "1px solid var(--border)", borderRadius: 6, background: "none", cursor: "pointer" }} />
          </label>
          <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 56, height: 40, borderRadius: 8, background: bg, color: accent, fontWeight: 800, fontSize: 15, border: "1px solid var(--border)" }} data-testid="poster-color-preview">Aa</div>
          {b.brand_set && <button className="sr-btn sr-btn-ghost sr-btn-sm" disabled={busy} onClick={resetColors} data-testid="poster-colors-reset"><RefreshCw size={12} /> Default</button>}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          {PRESETS.map((p) => (
            <button key={p.bg + p.accent} onClick={() => saveColors(p.bg, p.accent)} disabled={busy} title="Use this palette"
              data-testid={`poster-preset-${p.bg.slice(1)}`}
              style={{ width: 30, height: 30, borderRadius: 8, background: p.bg, border: bg === p.bg && accent === p.accent ? "2px solid var(--navy)" : "1px solid var(--border)", cursor: "pointer", position: "relative", padding: 0 }}>
              <span style={{ position: "absolute", right: 4, bottom: 4, width: 10, height: 10, borderRadius: 999, background: p.accent }} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}


export function OfferDetail({ tab: initialTab }) {
  const { id } = useParams();
  const nav = useNavigate();
  const [tab, setTab] = useState(initialTab || "overview");
  const [o, setO] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmSend, setConfirmSend] = useState(null);
  const load = () => sr.offerStudio.get(id).then(setO).catch(() => toast.error("Offer not found"));
  useEffect(() => { load(); }, [id]);
  useEffect(() => { if (initialTab) setTab(initialTab); }, [initialTab]);

  if (!o) return <div className="sr-card"><Loader2 className="sr-spin" /></div>;
  const assets = o.assets_urls || {};
  const TABS = [["overview", "Overview"], ["design", "Design"], ["landing-page", "Landing page"], ["distribution", "Distribution"], ["redemptions", "Redemptions"]];

  const act = async (fn, msg) => { setBusy(true); try { await fn(); if (msg) toast.success(msg); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); } };
  const startPublish = async () => {
    const cfg = o.auto_send || {};
    if (!cfg.enabled) { act(() => sr.offerStudio.publish(id), "Published"); return; }
    let aud = null;
    try { aud = await sr.offerStudio.audiencePreview(cfg.segment || "all"); } catch (e) { /* ignore */ }
    setConfirmSend({ aud, cfg });
  };
  const doPublish = (skip) => { setConfirmSend(null); act(() => sr.offerStudio.publish(id, skip), skip ? "Published (not sent)" : "Published — sending now"); };
  const downloadPoster = async () => {
    setBusy(true);
    try { downloadBlob(await sr.offerStudio.offerPoster(id), `poster-${(o.code || id).slice(0, 12)}.png`); toast.success("Poster downloaded — ready to print"); }
    catch (e) { toast.error("Couldn't generate the poster"); }
    finally { setBusy(false); }
  };
  const downloadRadioLife = async (variant) => {
    setBusy(true);
    try { downloadBlob(await sr.offerStudio.radioLifePoster(variant), `radiolife-${variant}-poster.png`); toast.success("RadioLife poster downloaded"); }
    catch (e) { toast.error("Couldn't generate the RadioLife poster"); }
    finally { setBusy(false); }
  };
  const downloadPack = async () => {
    setBusy(true);
    try { downloadBlob(await sr.offerStudio.offerPosterPack(id), `poster-pack-${(o.code || id).slice(0, 12)}.pdf`); toast.success("Printable pack downloaded (PDF)"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't build the poster pack"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="offer-detail-page">
      <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => nav("/app/offers")} style={{ marginBottom: 12 }}><ArrowLeft size={14} /> All offers</button>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div><h1 style={{ fontSize: 26 }}>{o.headline}</h1><div style={{ marginTop: 6 }}><StatusBadge s={o.status} /> <span className="sr-badge sr-badge-gray">Code {o.code}</span></div></div>
        {o.status !== "PUBLISHED" && <button className="sr-btn sr-btn-primary" disabled={busy} onClick={startPublish} data-testid="offer-publish-btn"><ShieldCheck size={15} /> Publish offer</button>}
      </div>

      {confirmSend && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.5)", zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} data-testid="publish-confirm-modal">
          <div className="sr-card" style={{ maxWidth: 440, width: "100%" }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}><Zap size={16} color="var(--blue)" /><strong style={{ color: "var(--navy)" }}>Send this coupon on publish?</strong></div>
            <p className="sr-muted" style={{ fontSize: 14 }}>
              Auto-send is on for the <b>{SEGMENTS.find((s) => s.v === (confirmSend.cfg.segment || "all"))?.label || "All contacts"}</b> segment.
              {confirmSend.aud
                ? <> This will reach <b>~{confirmSend.aud.total}</b> contacts — {confirmSend.aud.reachable_sms} by SMS · {confirmSend.aud.reachable_email} by email.</>
                : <> We couldn&apos;t preview the audience, but it will send to your chosen segment.</>}
            </p>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 16 }}>
              <button className="sr-btn sr-btn-primary sr-btn-sm" disabled={busy} onClick={() => doPublish(false)} data-testid="confirm-publish-send"><Send size={14} /> Publish &amp; send</button>
              <button className="sr-btn sr-btn-ghost sr-btn-sm" disabled={busy} onClick={() => doPublish(true)} data-testid="confirm-publish-only">Publish without sending</button>
              <button className="sr-btn sr-btn-ghost sr-btn-sm" disabled={busy} onClick={() => setConfirmSend(null)} data-testid="confirm-cancel">Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 6, margin: "16px 0", flexWrap: "wrap", borderBottom: "1px solid var(--border)" }} data-testid="offer-tabs">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => { setTab(k); nav(`/app/offers/${id}${k === "overview" ? "" : "/" + k}`); }} data-testid={`tab-${k}`}
            style={{ background: "none", border: "none", borderBottom: tab === k ? "2px solid var(--blue)" : "2px solid transparent", color: tab === k ? "var(--navy)" : "var(--muted)", fontWeight: 700, padding: "8px 10px", cursor: "pointer" }}>{l}</button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="sr-grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div className="sr-card">
            {assets.coupon_png && <img src={assets.coupon_png} alt="coupon" data-testid="offer-coupon-img" style={{ width: "100%", borderRadius: 12, border: "1px solid var(--border)" }} />}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
              <button className="sr-btn sr-btn-primary sr-btn-sm" disabled={busy} onClick={downloadPoster} data-testid="offer-poster-btn"><Download size={14} /> Download table-tent poster</button>
              {o.status === "PUBLISHED" && <button className="sr-btn sr-btn-ghost sr-btn-sm" disabled={busy} onClick={downloadPack} data-testid="offer-poster-pack-btn"><Layers size={14} /> Printable pack (PDF)</button>}
            </div>
            <p className="sr-muted" style={{ fontSize: 12, marginTop: 8 }}>Print-ready poster with a big QR — put it on tables or the counter so customers can scan to claim.{o.status === "PUBLISHED" ? " The pack bundles a table tent, window cling and counter card in one PDF." : ""}</p>
            <PosterBranding />
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border)" }} data-testid="radiolife-posters">
              <div className="sr-eyebrow" style={{ marginBottom: 6, display: "inline-flex", alignItems: "center", gap: 6 }}><ScanLine size={13} /> RadioLife posters</div>
              <p className="sr-muted" style={{ fontSize: 12, marginBottom: 8 }}>Tell customers you accept RadioLife payments &amp; points — they download RadioLife and play a game to win at your business.</p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button className="sr-btn sr-btn-ghost sr-btn-sm" disabled={busy} onClick={() => downloadRadioLife("table")} data-testid="radiolife-table-btn"><Download size={13} /> Table tent</button>
                <button className="sr-btn sr-btn-ghost sr-btn-sm" disabled={busy} onClick={() => downloadRadioLife("window")} data-testid="radiolife-window-btn"><Download size={13} /> Window / door</button>
              </div>
            </div>
          </div>
          <div className="sr-card">
            <Field label="Headline" v={o.headline} on={(v) => act(() => sr.offerStudio.update(id, { headline: v }))} />
            <Field label="Description" v={o.description ?? o.offer_copy} on={(v) => act(() => sr.offerStudio.update(id, { description: v }))} />
            <Field label="Call to action" v={o.cta} on={(v) => act(() => sr.offerStudio.update(id, { cta: v }))} />
            <Field label="Fine print" v={o.fine_print} on={(v) => act(() => sr.offerStudio.update(id, { fine_print: v }))} />
            <Field label="Terms" v={o.terms} on={(v) => act(() => sr.offerStudio.update(id, { terms: v }))} />
            <div style={{ marginTop: 10, padding: 12, background: "#F7F8FB", borderRadius: 10 }} data-testid="offer-rules">
              <div className="sr-eyebrow" style={{ marginBottom: 6 }}>Rules & limits</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                <span className="sr-badge sr-badge-gray">{((o.redemption_mode || o.code_mode) === "reusable" || o.code_mode === "REUSABLE") ? "Reusable code" : "Single-use code"}</span>
                {o.minimum_purchase != null && <span className="sr-badge sr-badge-gray">Min ${o.minimum_purchase}</span>}
                {o.max_total_redemptions != null && <span className="sr-badge sr-badge-gray">Max {o.max_total_redemptions} total</span>}
                {o.max_redemptions_per_customer != null && <span className="sr-badge sr-badge-gray">{o.max_redemptions_per_customer}/customer</span>}
                {o.daily_redemption_limit != null && <span className="sr-badge sr-badge-gray">{o.daily_redemption_limit}/day</span>}
                {o.new_customer_only && <span className="sr-badge sr-badge-gray">New customers</span>}
                {o.repeat_customer_only && <span className="sr-badge sr-badge-gray">Returning only</span>}
              </div>
              <p className="sr-muted" style={{ fontSize: 12, marginTop: 8 }}>
                {o.start_at ? `Starts ${new Date(o.start_at).toLocaleString()} · ` : ""}
                Expires: {o.expires_at_computed ? new Date(o.expires_at_computed).toLocaleDateString() : `${o.expiration_days} days`}
              </p>
            </div>
            <div style={{ marginTop: 10, padding: 12, background: "var(--blue-light)", borderRadius: 10 }} data-testid="offer-economics">
              <div className="sr-eyebrow" style={{ marginBottom: 6 }}>Internal economics (private)</div>
              <p style={{ fontSize: 13 }}>Face value: <b>{o.face_value != null ? `$${o.face_value}` : "—"}</b> · Margin floor: <b style={{ color: o.margin_floor_ok === false ? "#B42318" : "#067647" }}>{o.margin_floor_ok === false ? "at risk" : "ok"}</b></p>
            </div>
            <AutoSendCard offer={o} onChange={load} />
          </div>
        </div>
      )}

      {tab === "design" && (
        <div>
          <button className="sr-btn sr-btn-primary sr-btn-sm" disabled={busy} onClick={() => act(() => sr.offerStudio.design(id), "Creatives generated")} data-testid="offer-design-btn"><Palette size={14} /> Generate coupon + banners</button>
          {assets.coupon_png && <div className="sr-card" style={{ marginTop: 14 }}><div className="sr-eyebrow" style={{ marginBottom: 8 }}>Coupon</div><img src={assets.coupon_png} alt="coupon" style={{ maxWidth: 480, width: "100%", borderRadius: 10, border: "1px solid var(--border)" }} /></div>}
          {(o.banner_urls || []).length > 0 && (
            <div className="sr-card" style={{ marginTop: 14 }} data-testid="offer-banners">
              <div className="sr-eyebrow" style={{ marginBottom: 8, display: "flex", gap: 6, alignItems: "center" }}><Megaphone size={14} /> Display banner variants ({o.banner_urls.length})</div>
              <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))", gap: 12 }}>
                {o.banner_urls.map((b, i) => (
                  <div key={i} data-testid={`banner-${b.angle}-${b.size}`}>
                    <span className="sr-badge sr-badge-gray" style={{ marginBottom: 6 }}>{b.angle} · {b.size}</span>
                    <img src={b.url} alt={b.angle} style={{ width: "100%", borderRadius: 8, border: "1px solid var(--border)" }} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "landing-page" && (
        <div className="sr-card" data-testid="offer-landing-tab">
          <div className="sr-eyebrow" style={{ marginBottom: 8 }}>Hosted offer landing page</div>
          <p className="sr-muted" style={{ fontSize: 13 }}>Banners click through here. Customers add to wallet, download, share or get it texted/emailed.</p>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
            <code style={{ background: "var(--blue-light)", padding: "6px 10px", borderRadius: 8, fontSize: 12 }} data-testid="landing-url">{o.landing_url}</code>
            <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => { navigator.clipboard.writeText(o.landing_url); toast.success("Copied"); }}><Copy size={13} /> Copy</button>
            <a className="sr-btn sr-btn-primary sr-btn-sm" href={o.landing_url} target="_blank" rel="noreferrer" data-testid="open-landing"><ExternalLink size={13} /> Open landing page</a>
          </div>
          {assets.qr_png && <img src={assets.qr_png} alt="QR" style={{ width: 160, marginTop: 16 }} data-testid="landing-qr" />}
        </div>
      )}

      {tab === "distribution" && <Distribution o={o} onSave={(ch) => act(() => sr.offerStudio.distribution(id, ch), "Distribution saved")} onAttach={() => act(() => sr.offerStudio.attachCampaign(id), "Attached to a display campaign")} busy={busy} />}

      {tab === "redemptions" && <Redemptions o={o} id={id} reload={load} />}
    </div>
  );
}

const Field = ({ label, v, on }) => {
  const [val, setVal] = useState(v || "");
  useEffect(() => { setVal(v || ""); }, [v]);
  return (
    <div className="sr-field">
      <label className="sr-label">{label}</label>
      <input className="sr-input" value={val} onChange={(e) => setVal(e.target.value)} onBlur={() => val !== v && on(val)} data-testid={`edit-${label.toLowerCase().replace(/\s/g, "-")}`} />
    </div>
  );
};

function Distribution({ o, onSave, onAttach, busy }) {
  const [ch, setCh] = useState(o.distribution || []);
  const toggle = (c) => setCh((p) => p.includes(c) ? p.filter((x) => x !== c) : [...p, c]);
  return (
    <div className="sr-card" data-testid="offer-distribution-tab">
      <div className="sr-eyebrow" style={{ marginBottom: 8 }}>Distribution channels</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {DISTRIB.map((c) => (
          <button key={c} onClick={() => toggle(c)} data-testid={`channel-${c}`}
            style={{ borderRadius: 999, padding: "6px 14px", fontWeight: 700, fontSize: 13, cursor: "pointer",
              border: ch.includes(c) ? "1px solid var(--blue)" : "1px solid var(--border)", background: ch.includes(c) ? "var(--blue)" : "#fff", color: ch.includes(c) ? "#fff" : "var(--navy)" }}>{c}</button>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
        <button className="sr-btn sr-btn-primary sr-btn-sm" disabled={busy} onClick={() => onSave(ch)} data-testid="save-distribution"><LinkIcon size={14} /> Save channels</button>
        <button className="sr-btn sr-btn-ghost sr-btn-sm" disabled={busy} onClick={onAttach} data-testid="attach-campaign-btn"><Megaphone size={14} /> Create display campaign with banners</button>
      </div>
      {o.campaign_id && <p className="sr-muted" style={{ fontSize: 12, marginTop: 10 }}>Attached to display campaign · banners click through to the hosted landing page.</p>}
      <p className="sr-muted" style={{ fontSize: 12, marginTop: 8 }}><Layers size={12} /> The same offer can also be pushed to RadioLife.app and Loyalty later — one offer, many surfaces.</p>
    </div>
  );
}

function Redemptions({ o, id, reload }) {
  const [data, setData] = useState(null);
  const [sends, setSends] = useState(null);
  const [code, setCode] = useState(o.code || "");
  const [cust, setCust] = useState("");
  const [amount, setAmount] = useState("");
  const load = () => {
    sr.offerStudio.redemptions(id).then(setData).catch(() => {});
    sr.offerStudio.offerSends(id).then(setSends).catch(() => setSends(null));
  };
  useEffect(() => { load(); }, [id]);
  const doRedeem = async () => {
    try {
      const body = { code, idempotency_key: `manual_${Date.now()}` };
      if (cust.trim()) body.customer_ref = cust.trim();
      if (amount) body.purchase_amount = Number(amount);
      const r = await sr.offerStudio.redeem(id, body);
      toast.success(r.idempotent ? "Already recorded (idempotent)" : "Redemption recorded"); setCust(""); setAmount(""); load(); reload && reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not redeem"); }
  };
  const a = data?.analytics;
  const attr = sends?.attribution;
  const box = (label, val, c) => (
    <div style={{ flex: 1, minWidth: 120, background: "#fff", border: "1px solid var(--border)", borderRadius: 10, padding: 12 }}>
      <div style={{ fontSize: 24, fontWeight: 800, color: c }}>{val}</div><div className="sr-muted" style={{ fontSize: 12 }}>{label}</div>
    </div>
  );
  return (
    <div data-testid="offer-redemptions-tab">
      <div className="sr-card">
        <div className="sr-eyebrow" style={{ marginBottom: 8 }}>Observed engagement</div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {box("Viewed", a?.observed.viewed ?? "—", "var(--navy)")}
          {box("Claimed", a?.observed.claimed ?? "—", "var(--navy)")}
          {box("Wallet saved", a?.observed.wallet_saved ?? "—", "var(--navy)")}
          {box("Downloaded", a?.observed.coupon_downloaded ?? "—", "var(--navy)")}
          {box("Shared", a?.observed.shared ?? "—", "var(--navy)")}
        </div>
        <div className="sr-eyebrow" style={{ margin: "16px 0 8px" }}>Verified outcomes <span className="sr-muted" style={{ fontWeight: 400, fontSize: 11 }}>(kept separate from observed & projected)</span></div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {box("Redeemed", a?.verified.redeemed ?? "—", "#067647")}
          {box("Verified purchases", a?.verified.verified_purchase ?? "—", "#067647")}
        </div>
      </div>

      <div className="sr-card" style={{ marginTop: 14 }}>
        <div className="sr-eyebrow" style={{ marginBottom: 8 }}>Redeem a coupon (staff)</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div className="sr-field" style={{ margin: 0 }}><label className="sr-label">Code</label>
            <input className="sr-input" style={{ maxWidth: 180 }} value={code} onChange={(e) => setCode(e.target.value)} data-testid="redeem-code-input" /></div>
          <div className="sr-field" style={{ margin: 0 }}><label className="sr-label">Customer email/phone (optional)</label>
            <input className="sr-input" style={{ maxWidth: 200 }} value={cust} onChange={(e) => setCust(e.target.value)} data-testid="redeem-customer-input" placeholder="for per-customer rules + ROI" /></div>
          <div className="sr-field" style={{ margin: 0 }}><label className="sr-label">Purchase $ (optional)</label>
            <input className="sr-input" type="number" style={{ maxWidth: 120 }} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="redeem-amount-input" /></div>
          <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={doRedeem} data-testid="redeem-btn" disabled={o.status !== "PUBLISHED"}><Ticket size={14} /> Redeem</button>
        </div>
        {o.status !== "PUBLISHED" && <p className="sr-muted" style={{ fontSize: 12, marginTop: 8 }}>Publish the offer before redeeming.</p>}
        <p className="sr-muted" style={{ fontSize: 12, marginTop: 8 }}>Tip: store staff can scan coupons hands-free from the SmartReachAI mobile app&apos;s Redeem tab.</p>
      </div>

      {sends && sends.summary.total > 0 && (
        <div className="sr-card" style={{ marginTop: 14 }} data-testid="auto-send-history">
          <div className="sr-eyebrow" style={{ marginBottom: 8, display: "flex", gap: 6, alignItems: "center" }}><Send size={13} /> Auto-send delivery log</div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
            {box("Sent", sends.summary.sent, "#067647")}
            {box("Failed", sends.summary.failed, sends.summary.failed ? "#B42318" : "var(--navy)")}
            {attr && box("Redeemed from sends", attr.redeemed, "var(--navy)")}
            {attr && box("Revenue per blast", `$${attr.revenue}`, "#067647")}
          </div>
          <DeliveryInsights sends={sends.sends} />
          <div style={{ maxHeight: 240, overflow: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead><tr style={{ textAlign: "left", color: "var(--muted)" }}><th style={{ padding: 6 }}>Recipient</th><th>Channel</th><th>Status</th><th>Delivery</th><th>When</th></tr></thead>
              <tbody>{sends.sends.slice(0, 100).map((s) => (
                <tr key={s.id} style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={{ padding: 6 }}>{s.to}</td>
                  <td>{(s.method || "").toUpperCase()}</td>
                  <td><span className="sr-badge" style={{ background: s.status === "sent" ? "#E7F6EC" : "#FDECEC", color: s.status === "sent" ? "#067647" : "#B42318" }}>{s.status === "sent" ? "sent" : (s.reason ? "failed" : "failed")}</span></td>
                  <td>{deliveryBadge(s)}</td>
                  <td className="sr-muted">{s.created_at ? new Date(s.created_at).toLocaleString() : ""}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
          <p className="sr-muted" style={{ fontSize: 11, marginTop: 8 }}>Live Mailgun bounce tracking is on — the Delivery column updates as the mailbox reports delivered, opened, bounced or complained.</p>
        </div>
      )}
    </div>
  );
}

// ---------------- Wallet passes ----------------
export function WalletPasses() {
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  useEffect(() => { sr.offerStudio.walletPasses().then((r) => setRows(r.wallet_passes || [])).catch(() => setRows([])); }, []);
  return (
    <div data-testid="wallet-passes-page">
      <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => nav("/app/offers")} style={{ marginBottom: 12 }}><ArrowLeft size={14} /> Back</button>
      <h1 style={{ fontSize: 26 }}>Wallet passes</h1>
      <p className="sr-muted" style={{ marginTop: 6, marginBottom: 16 }}>Apple & Google Wallet passes issued from your offers.</p>
      {!rows && <div className="sr-card"><Loader2 className="sr-spin" /></div>}
      {rows && rows.length === 0 && <div className="sr-card" data-testid="wallet-passes-empty"><p className="sr-muted">No wallet passes issued yet. When a customer taps “Add to Wallet” on an offer landing page, it appears here.</p></div>}
      <div style={{ display: "grid", gap: 10 }}>
        {(rows || []).map((p) => (
          <div key={p.id} className="sr-card" data-testid={`wallet-pass-${p.id}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }} onClick={() => nav(`/app/wallet-passes/${p.id}`)}>
            <div><b style={{ color: "var(--navy)" }}>{p.provider === "apple" ? "Apple Wallet" : "Google Wallet"}</b><div className="sr-muted" style={{ fontSize: 12 }}>Object {p.object_id}</div></div>
            <span className="sr-badge sr-badge-gray">{p.demo ? "demo" : "live"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function WalletPassDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  useEffect(() => { sr.offerStudio.walletPass(id).then(setP).catch(() => toast.error("Not found")); }, [id]);
  return (
    <div data-testid="wallet-pass-detail">
      <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => nav("/app/wallet-passes")} style={{ marginBottom: 12 }}><ArrowLeft size={14} /> Back</button>
      {!p ? <div className="sr-card"><Loader2 className="sr-spin" /></div> : (
        <div className="sr-card">
          <h1 style={{ fontSize: 22 }}>{p.provider === "apple" ? "Apple Wallet pass" : "Google Wallet pass"}</h1>
          <p className="sr-muted" style={{ fontSize: 13, marginTop: 8 }}>Object ID: {p.object_id}</p>
          <p className="sr-muted" style={{ fontSize: 13 }}>Offer: {p.offer_id} · {p.demo ? "Demo mode" : "Live"}</p>
        </div>
      )}
    </div>
  );
}

// ---------------- Wallet Settings (go live) ----------------
const fileToB64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(",")[1] || "");
  r.onerror = reject;
  r.readAsDataURL(file);
});
const fileToText = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result));
  r.onerror = reject;
  r.readAsText(file);
});

const LiveBadge = ({ live }) => (
  <span className="sr-badge" style={{ background: live ? "#E7F6EC" : "#FCF1DC", color: live ? "#067647" : "#9A7412" }}>
    {live ? <><CheckCircle2 size={11} /> Live</> : <><ShieldCheck size={11} /> Demo mode</>}
  </span>
);

export function WalletSettings() {
  const nav = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState("");
  const [apple, setApple] = useState({ team_id: "", pass_type_id: "", p12_password: "" });
  const [google, setGoogle] = useState({ issuer_id: "", origins: "" });
  const [p12File, setP12File] = useState(null);
  const [wwdrFile, setWwdrFile] = useState(null);
  const [saFile, setSaFile] = useState(null);

  const load = () => sr.offerStudio.walletConfig().then((c) => {
    setCfg(c);
    setApple({ team_id: c.apple.team_id || "", pass_type_id: c.apple.pass_type_id || "", p12_password: "" });
    setGoogle({ issuer_id: c.google.issuer_id || "", origins: (c.google.origins || []).join(", ") });
  }).catch(() => toast.error("Could not load wallet settings"));
  useEffect(() => { load(); }, []);

  const saveApple = async () => {
    setBusy("apple");
    try {
      const body = { apple: { team_id: apple.team_id, pass_type_id: apple.pass_type_id } };
      if (apple.p12_password) body.apple.p12_password = apple.p12_password;
      if (p12File) body.apple.p12_b64 = await fileToB64(p12File);
      if (wwdrFile) body.apple.wwdr_b64 = await fileToB64(wwdrFile);
      await sr.offerStudio.saveWalletConfig(body);
      toast.success("Apple Wallet settings saved"); setP12File(null); setWwdrFile(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); } finally { setBusy(""); }
  };
  const saveGoogle = async () => {
    setBusy("google");
    try {
      const body = { google: { issuer_id: google.issuer_id, origins: google.origins } };
      if (saFile) {
        const txt = await fileToText(saFile);
        try { body.google.sa_json = JSON.parse(txt); }
        catch { toast.error("That service-account file isn't valid JSON"); setBusy(""); return; }
      }
      await sr.offerStudio.saveWalletConfig(body);
      toast.success("Google Wallet settings saved"); setSaFile(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); } finally { setBusy(""); }
  };
  const clear = async (provider) => {
    setBusy(provider);
    try { await sr.offerStudio.clearWalletConfig(provider); toast.success(`${provider === "apple" ? "Apple" : "Google"} credentials removed — back to demo mode`); load(); }
    catch { toast.error("Could not remove"); } finally { setBusy(""); }
  };

  if (!cfg) return <div className="sr-card"><Loader2 className="sr-spin" /></div>;
  const fileRow = (label, file, setFile, uploaded, testid) => (
    <div className="sr-field">
      <label className="sr-label">{label} {uploaded && !file && <span style={{ color: "#067647", fontWeight: 700 }}>· on file ✓</span>}</label>
      <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid={testid}
        style={{ fontSize: 13 }} />
      {file && <span className="sr-muted" style={{ fontSize: 12 }}><Upload size={11} /> {file.name}</span>}
    </div>
  );

  return (
    <div data-testid="wallet-settings-page">
      <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => nav("/app/offers")} style={{ marginBottom: 12 }}><ArrowLeft size={14} /> Back</button>
      <span className="sr-badge" style={{ marginBottom: 8 }}><Wallet size={12} /> Wallet Settings</span>
      <h1 style={{ fontSize: 26 }}>Go live with wallet passes</h1>
      <p className="sr-muted" style={{ marginTop: 6, marginBottom: 8, maxWidth: 720 }}>
        Until you add credentials, Apple &amp; Google passes work in <b>demo mode</b> — correctly shaped, but not installable on a real device.
        Add your credentials below to issue real, installable passes. Everything is stored securely and survives redeploys.
      </p>

      <div className="sr-grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 12 }}>
        {/* Apple */}
        <div className="sr-card" data-testid="apple-wallet-card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={{ fontWeight: 800, color: "var(--navy)", display: "flex", gap: 8, alignItems: "center" }}><Wallet size={16} /> Apple Wallet</div>
            <LiveBadge live={cfg.apple.live} />
          </div>
          <div className="sr-field"><label className="sr-label">Team ID</label>
            <input className="sr-input" placeholder="10-char Apple Team ID" value={apple.team_id} onChange={(e) => setApple({ ...apple, team_id: e.target.value })} data-testid="apple-team-id" /></div>
          <div className="sr-field"><label className="sr-label">Pass Type ID</label>
            <input className="sr-input" placeholder="pass.us.yourbrand.offers" value={apple.pass_type_id} onChange={(e) => setApple({ ...apple, pass_type_id: e.target.value })} data-testid="apple-pass-type-id" /></div>
          {fileRow("Pass Type certificate (.p12)", p12File, setP12File, cfg.apple.p12_uploaded, "apple-p12-file")}
          <div className="sr-field"><label className="sr-label"><KeyRound size={11} /> .p12 export password {cfg.apple.has_password && <span style={{ color: "#067647", fontWeight: 700 }}>· set ✓</span>}</label>
            <input className="sr-input" type="password" placeholder="•••• (leave blank to keep)" value={apple.p12_password} onChange={(e) => setApple({ ...apple, p12_password: e.target.value })} data-testid="apple-p12-password" /></div>
          {fileRow("Apple WWDR certificate", wwdrFile, setWwdrFile, cfg.apple.wwdr_uploaded, "apple-wwdr-file")}
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button className="sr-btn sr-btn-primary sr-btn-sm" disabled={busy === "apple"} onClick={saveApple} data-testid="save-apple-wallet">{busy === "apple" ? <Loader2 size={14} className="sr-spin" /> : <><ShieldCheck size={14} /> Save Apple</>}</button>
            {(cfg.apple.team_id || cfg.apple.p12_uploaded) && <button className="sr-btn sr-btn-ghost sr-btn-sm" disabled={busy === "apple"} onClick={() => clear("apple")} data-testid="clear-apple-wallet"><Trash2 size={13} /> Remove</button>}
          </div>
        </div>

        {/* Google */}
        <div className="sr-card" data-testid="google-wallet-card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={{ fontWeight: 800, color: "var(--navy)", display: "flex", gap: 8, alignItems: "center" }}><Wallet size={16} /> Google Wallet</div>
            <LiveBadge live={cfg.google.live} />
          </div>
          <div className="sr-field"><label className="sr-label">Issuer ID</label>
            <input className="sr-input" placeholder="Issuer ID from the Wallet console" value={google.issuer_id} onChange={(e) => setGoogle({ ...google, issuer_id: e.target.value })} data-testid="google-issuer-id" /></div>
          {fileRow("Service-account key (.json)", saFile, setSaFile, cfg.google.sa_uploaded, "google-sa-file")}
          {cfg.google.sa_client_email && <p className="sr-muted" style={{ fontSize: 12 }}>Service account: {cfg.google.sa_client_email}</p>}
          <div className="sr-field"><label className="sr-label">Allowed origins (comma-separated)</label>
            <input className="sr-input" placeholder="https://yourbrand.com" value={google.origins} onChange={(e) => setGoogle({ ...google, origins: e.target.value })} data-testid="google-origins" /></div>
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button className="sr-btn sr-btn-primary sr-btn-sm" disabled={busy === "google"} onClick={saveGoogle} data-testid="save-google-wallet">{busy === "google" ? <Loader2 size={14} className="sr-spin" /> : <><ShieldCheck size={14} /> Save Google</>}</button>
            {(cfg.google.issuer_id || cfg.google.sa_uploaded) && <button className="sr-btn sr-btn-ghost sr-btn-sm" disabled={busy === "google"} onClick={() => clear("google")} data-testid="clear-google-wallet"><Trash2 size={13} /> Remove</button>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------- POS Revenue Matchback ----------------
function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return [];
  const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
  return lines.slice(1).map((line) => {
    const cells = line.split(",");
    const row = {};
    headers.forEach((h, i) => { row[h] = (cells[i] || "").trim(); });
    return row;
  });
}

export function OfferMatchback() {
  const nav = useNavigate();
  const location = useLocation();
  const [cfg, setCfg] = useState(null);
  const [summary, setSummary] = useState(null);
  const [clover, setClover] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => {
    sr.offerStudio.posConfig().then(setCfg).catch(() => {});
    sr.offerStudio.posSummary().then(setSummary).catch(() => {});
    sr.offerStudio.cloverStatus().then(setClover).catch(() => setClover(null));
    sr.offerStudio.refundAlerts().then(setAlerts).catch(() => setAlerts(null));
  };
  useEffect(() => { load(); }, []);
  const markAlertsRead = async () => {
    try { await sr.offerStudio.markRefundAlertsRead(); setAlerts({ ...alerts, unread: 0, alerts: (alerts?.alerts || []).map((a) => ({ ...a, read: true })) }); }
    catch { /* noop */ }
  };
  useEffect(() => {
    const p = new URLSearchParams(location.search).get("clover");
    if (p === "connected") toast.success("Clover connected — orders will now match back to revenue.");
    else if (p === "error") toast.error("Clover connection failed. Please try again.");
  }, [location.search]);

  const connectClover = async () => {
    setBusy("cv");
    try { const r = await sr.offerStudio.cloverConnect(); if (r.url) window.location.href = r.url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Clover connect failed"); setBusy(""); }
  };
  const disconnectClover = async () => {
    setBusy("cv");
    try { await sr.offerStudio.cloverDisconnect(); setClover({ ...clover, connected: false, merchant_id: null }); toast.success("Clover disconnected"); }
    catch { toast.error("Could not disconnect"); }
    finally { setBusy(""); }
  };

  const onCsv = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy("csv");
    try {
      const rows = parseCsv(await fileToText(file));
      if (!rows.length) { toast.error("No rows found in CSV"); setBusy(""); return; }
      const r = await sr.offerStudio.posIngest(rows);
      toast.success(`Matched ${r.matched} · $${r.revenue_matched} revenue · ${r.unmatched} unmatched`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not ingest CSV"); }
    finally { setBusy(""); e.target.value = ""; }
  };
  const rotate = async () => { try { const r = await sr.offerStudio.rotatePosToken(); setCfg({ ...cfg, ...r }); toast.success("Webhook URL rotated"); } catch { toast.error("Could not rotate"); } };
  const connectSquare = async () => { setBusy("sq"); try { const r = await sr.offerStudio.squareConnect({}); setCfg({ ...cfg, square: { ...cfg?.square, ...r } }); if (r.connected) toast.success("Square connected — orders will now match back to revenue."); else toast.error(r.reason || "Square connect failed"); } catch (e) { toast.error(e?.response?.data?.detail || "Square connect failed"); } finally { setBusy(""); } };
  const disconnectSquare = async () => { setBusy("sq"); try { await sr.offerStudio.squareDisconnect(); setCfg({ ...cfg, square: { ...cfg?.square, connected: false, merchant_id: null } }); toast.success("Square disconnected"); } catch { toast.error("Could not disconnect"); } finally { setBusy(""); } };
  const provisionSquareWebhook = async () => { setBusy("sqwh"); try { const r = await sr.offerStudio.squareCreateWebhook(); if (r.ok) toast.success(`Square webhook ${r.action} — live revenue matchback is on.`); else toast.error(r.reason || "Could not provision webhook"); } catch (e) { toast.error(e?.response?.data?.detail || "Could not provision webhook"); } finally { setBusy(""); } };

  const box = (label, val, c) => (
    <div style={{ flex: 1, minWidth: 140, background: "#fff", border: "1px solid var(--border)", borderRadius: 10, padding: 14 }}>
      <div style={{ fontSize: 26, fontWeight: 800, color: c }}>{val}</div><div className="sr-muted" style={{ fontSize: 12 }}>{label}</div>
    </div>
  );
  return (
    <div data-testid="offer-matchback-page">
      <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => nav("/app/offers")} style={{ marginBottom: 12 }}><ArrowLeft size={14} /> Back</button>
      <span className="sr-badge" style={{ marginBottom: 8 }}><DollarSign size={12} /> POS Revenue Matchback</span>
      <h1 style={{ fontSize: 26 }}>Tie redemptions to real revenue</h1>
      <p className="sr-muted" style={{ marginTop: 6, marginBottom: 14, maxWidth: 720 }}>Connect your POS to attribute actual purchase revenue back to each coupon — via Square, Clover, a webhook, or a CSV upload. Revenue and ROI below update automatically as orders flow in.</p>

      <div className="sr-card" style={{ marginBottom: 16, background: "var(--blue-light)", borderColor: "var(--blue)" }} data-testid="order-tagging-tip">
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
          <Info size={16} style={{ marginTop: 2, color: "var(--navy)" }} />
          <div>
            <b style={{ color: "var(--navy)" }}>Tip: tag the ticket so revenue matches</b>
            <p className="sr-muted" style={{ fontSize: 13, marginTop: 4, marginBottom: 0 }}>
              At checkout, have staff type the coupon code (e.g. <code>SR-8F3A21</code>) into the order&apos;s <b>Note</b> or <b>reference</b> field on your Square/Clover ticket — or add it as the discount name. We read that code from each order and match its revenue back to the offer automatically.
            </p>
          </div>
        </div>
      </div>

      {summary && (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }} data-testid="matchback-totals">
          {box("Revenue matched", `$${summary.totals?.revenue_matched ?? 0}`, "#067647")}
          {box("Net profit", `$${summary.totals?.net ?? 0}`, (summary.totals?.net ?? 0) >= 0 ? "#067647" : "#B42318")}
          {box("Blended ROI", summary.totals?.roi != null ? `${summary.totals.roi}x` : "—", "var(--navy)")}
          {box("Matched orders", summary.totals?.orders ?? 0, "var(--navy)")}
          {box("Refunded orders", summary.totals?.refunded_orders ?? 0, (summary.totals?.refunded_orders ?? 0) > 0 ? "#9A7412" : "var(--navy)")}
        </div>
      )}

      {summary && (summary.totals?.orders ?? 0) > 0 && (
        <div className="sr-card" style={{ marginBottom: 16 }} data-testid="verified-sales-card">
          <div className="sr-eyebrow" style={{ marginBottom: 4, display: "flex", gap: 6, alignItems: "center" }}>
            <ShieldCheck size={14} /> Verified sales &amp; contribution
            <span className="sr-badge" style={{ background: "#E7F6EC", color: "#067647", marginLeft: 4 }} data-testid="verified-badge">VERIFIED · POS-matched</span>
          </div>
          <p className="sr-muted" style={{ fontSize: 12, marginTop: 0, marginBottom: 12 }}>Real revenue matched from your POS — gross, refunds and net, with contribution after offer cost and payment-processing fees.</p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }} data-testid="verified-sales-totals">
            {box("Gross sales", `$${(summary.totals?.gross ?? 0).toLocaleString()}`, "var(--navy)")}
            {box("Refunds", `−$${(summary.totals?.refunds ?? 0).toLocaleString()}`, (summary.totals?.refunds ?? 0) > 0 ? "#B42318" : "var(--muted)")}
            {box("Net sales", `$${(summary.totals?.net_sales ?? 0).toLocaleString()}`, "#067647")}
            {box("Contribution (after fees)", `$${(summary.totals?.contribution ?? 0).toLocaleString()}`, (summary.totals?.contribution ?? 0) >= 0 ? "#067647" : "#B42318")}
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 12, fontSize: 13 }} data-testid="verified-sales-breakdown">
            <span className="sr-badge sr-badge-gray" data-testid="vs-repeat">Repeat customers: <b style={{ color: "var(--navy)" }}>{summary.totals?.repeat_customers ?? 0}</b>{summary.totals?.unique_customers ? ` of ${summary.totals.unique_customers}` : ""}{summary.totals?.repeat_rate != null ? ` · ${summary.totals.repeat_rate}%` : ""}</span>
            <span className="sr-badge sr-badge-gray" data-testid="vs-fees">Processing fees: −${(summary.totals?.processing_fees ?? 0).toLocaleString()}{summary.totals?.fee_pct ? ` (${summary.totals.fee_pct}%)` : ""}</span>
            <span className="sr-badge sr-badge-gray" data-testid="vs-cost">Offer cost: −${(summary.totals?.estimated_cost ?? 0).toLocaleString()}</span>
            {summary.totals?.contribution_margin_pct != null && <span className="sr-badge" style={{ background: "#E7F6EC", color: "#067647" }} data-testid="vs-margin">Contribution margin: {summary.totals.contribution_margin_pct}%</span>}
          </div>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" style={{ marginTop: 12 }} data-testid="sales-digest-btn"
            onClick={async () => {
              try {
                const r = await sr.offerStudio.salesDigestTest();
                r.sent ? toast.success(`Sales recap emailed to ${r.to}`)
                       : toast.error(r.reason === "no_recipient_or_mailgun" ? "Email isn't configured yet" : "No matched orders to send yet");
              } catch (e) { toast.error(e?.response?.data?.detail || "Could not send recap"); }
            }}><Send size={13} /> Email me this recap</button>
          {(summary.recent || []).length > 0 && (
            <div style={{ marginTop: 14, overflowX: "auto" }} data-testid="order-ledger">
              <div className="sr-eyebrow" style={{ marginBottom: 8 }}>Order ledger · latest {Math.min(summary.recent.length, 12)}</div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead><tr style={{ textAlign: "left", color: "var(--muted)" }}><th style={{ padding: 8 }}>Date</th><th>Code</th><th>Order</th><th style={{ textAlign: "right" }}>Gross</th><th style={{ textAlign: "right" }}>Refund</th><th style={{ textAlign: "right", paddingRight: 8 }}>Net</th></tr></thead>
                <tbody>{summary.recent.slice(0, 12).map((m) => {
                  const net = m.revenue ?? 0;
                  const ref = m.refunded_amount ?? 0;
                  const gr = m.gross_revenue != null ? m.gross_revenue : net + ref;
                  return (
                    <tr key={m.id} style={{ borderTop: "1px solid var(--border)" }} data-testid="ledger-row">
                      <td style={{ padding: 8 }}>{String(m.occurred_at || m.created_at || "").slice(0, 10)}</td>
                      <td><code style={{ fontSize: 11 }}>{m.code}</code></td>
                      <td className="sr-muted">{m.order_id || "—"}</td>
                      <td style={{ textAlign: "right" }}>${gr.toLocaleString()}</td>
                      <td style={{ textAlign: "right", color: ref > 0 ? "#B42318" : "var(--muted)" }}>{ref > 0 ? `−$${ref.toLocaleString()}` : "—"}</td>
                      <td style={{ textAlign: "right", fontWeight: 700, color: "#067647", paddingRight: 8 }}>${net.toLocaleString()}</td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {alerts && (alerts.alerts || []).length > 0 && (
        <div className="sr-card" style={{ marginBottom: 16, borderColor: "#E4B84B" }} data-testid="refund-alerts-card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <div className="sr-eyebrow" style={{ display: "flex", gap: 6, alignItems: "center", color: "#9A7412" }}>
              <AlertTriangle size={14} /> Refund alerts {alerts.unread > 0 && <span className="sr-badge" style={{ background: "#FDECEC", color: "#B42318" }} data-testid="refund-unread-badge">{alerts.unread} new</span>}
            </div>
            {alerts.unread > 0 && <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={markAlertsRead} data-testid="mark-alerts-read">Mark all read</button>}
          </div>
          <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
            {(alerts.alerts || []).slice(0, 8).map((a) => (
              <div key={a.id} data-testid="refund-alert-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 8, background: a.read ? "transparent" : "#FCF6E8", border: "1px solid var(--border)" }}>
                <div style={{ minWidth: 0 }}>
                  <span style={{ fontWeight: 700, color: "var(--navy)" }}>{a.offer_name}</span>
                  <span className="sr-muted" style={{ fontSize: 12 }}> · {a.code}{a.order_id ? ` · order ${a.order_id}` : ""}</span>
                </div>
                <span className="sr-badge" style={{ background: "#FDECEC", color: "#B42318", whiteSpace: "nowrap" }}>−${a.refunded_amount} refunded</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {summary && ((summary.trend || []).length > 0 || (summary.leaderboard || []).length > 0) && (
        <div className="sr-grid" style={{ gridTemplateColumns: "1.4fr 1fr", gap: 16, marginBottom: 16 }} data-testid="matchback-insights">
          <div className="sr-card" data-testid="matchback-trend">
            <div className="sr-eyebrow" style={{ marginBottom: 10, display: "flex", gap: 6, alignItems: "center" }}><TrendingUp size={14} /> Daily revenue (matched)</div>
            {(summary.trend || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={summary.trend} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
                  <defs><linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#067647" stopOpacity={0.35} /><stop offset="100%" stopColor="#067647" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => [`$${v}`, "Revenue"]} />
                  <Area type="monotone" dataKey="revenue" stroke="#067647" strokeWidth={2} fill="url(#rev)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : <p className="sr-muted" style={{ fontSize: 13 }}>No matched revenue yet.</p>}
          </div>
          <div className="sr-card" data-testid="matchback-leaderboard">
            <div className="sr-eyebrow" style={{ marginBottom: 10, display: "flex", gap: 6, alignItems: "center" }}><Trophy size={14} /> Best-ROI offers</div>
            {(summary.leaderboard || []).length > 0 ? (summary.leaderboard || []).map((r, i) => (
              <div key={r.offer_id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderTop: i ? "1px solid var(--border)" : "none" }}>
                <span style={{ width: 24, height: 24, borderRadius: 999, background: i === 0 ? "#E4C066" : "#EEF1F5", color: i === 0 ? "#111827" : "var(--navy)", fontWeight: 800, fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center" }}>{i + 1}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "var(--navy)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</div>
                  <div className="sr-muted" style={{ fontSize: 11 }}>${r.revenue_matched} · {r.orders} orders</div>
                </div>
                <span className="sr-badge" style={{ background: "#E7F6EC", color: "#067647" }}>{r.roi != null ? `${r.roi}x` : "—"}</span>
              </div>
            )) : <p className="sr-muted" style={{ fontSize: 13 }}>No ROI data yet.</p>}
          </div>
        </div>
      )}

      {summary && (summary.by_location || []).length > 0 && (
        <div className="sr-card" style={{ marginBottom: 16 }} data-testid="matchback-locations">
          <div className="sr-eyebrow" style={{ marginBottom: 10, display: "flex", gap: 6, alignItems: "center" }}><MapPin size={14} /> Revenue by location</div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead><tr style={{ textAlign: "left", color: "var(--muted)" }}><th style={{ padding: 8 }}>Location</th><th>Orders</th><th>Revenue</th><th>Refunds</th><th>Share</th></tr></thead>
            <tbody>{summary.by_location.map((l, i) => {
              const top = summary.by_location[0]?.revenue || 1;
              return (
                <tr key={l.location_id || `loc-${i}`} style={{ borderTop: "1px solid var(--border)" }} data-testid="location-row">
                  <td style={{ padding: 8, fontWeight: 700, color: "var(--navy)" }}>{l.name}{i === 0 && summary.by_location.length > 1 && <span className="sr-badge" style={{ background: "#E7F6EC", color: "#067647", marginLeft: 6 }}>top</span>}</td>
                  <td>{l.orders}</td>
                  <td style={{ color: "#067647", fontWeight: 700 }}>${l.revenue}</td>
                  <td>{l.refunded_orders ? <span className="sr-badge" style={{ background: "#FCF1DC", color: "#9A7412" }}>{l.refunded_orders}</span> : <span className="sr-muted">—</span>}</td>
                  <td style={{ width: 140 }}><div style={{ height: 8, borderRadius: 999, background: "#EEF1F5", overflow: "hidden" }}><div style={{ width: `${Math.max(4, (l.revenue / top) * 100)}%`, height: "100%", background: "#067647" }} /></div></td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      )}

      <div className="sr-grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div className="sr-card" data-testid="matchback-webhook-card">
          <div className="sr-eyebrow" style={{ marginBottom: 8 }}>Real-time webhook</div>
          <p className="sr-muted" style={{ fontSize: 13 }}>POST each order to this URL with a JSON body: <code>{`{ code, revenue, order_id }`}</code>.</p>
          {cfg ? (
            <>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10, flexWrap: "wrap" }}>
                <code style={{ background: "var(--blue-light)", padding: "6px 10px", borderRadius: 8, fontSize: 11, wordBreak: "break-all", flex: 1 }} data-testid="webhook-url">{cfg.webhook_url}</code>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => { navigator.clipboard.writeText(cfg.webhook_url); toast.success("Copied"); }} data-testid="copy-webhook"><Copy size={13} /> Copy</button>
                <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={rotate} data-testid="rotate-webhook"><RefreshCw size={13} /> Rotate URL</button>
              </div>
            </>
          ) : <Loader2 className="sr-spin" />}
        </div>

        <div className="sr-card" data-testid="matchback-csv-card">
          <div className="sr-eyebrow" style={{ marginBottom: 8 }}>Upload a CSV of orders</div>
          <p className="sr-muted" style={{ fontSize: 13 }}>Columns: <b>{(cfg?.csv_columns || ["code", "revenue", "order_id"]).join(", ")}</b>. We match each row to a coupon by code.</p>
          <label className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 10, cursor: "pointer", display: "inline-flex" }} data-testid="csv-upload-label">
            {busy === "csv" ? <Loader2 size={14} className="sr-spin" /> : <><Upload size={14} /> Upload CSV</>}
            <input type="file" accept=".csv,text/csv" onChange={onCsv} style={{ display: "none" }} data-testid="csv-upload-input" />
          </label>
        </div>
      </div>

      <div className="sr-card" style={{ marginTop: 16 }} data-testid="matchback-square-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div className="sr-eyebrow" style={{ display: "flex", gap: 6, alignItems: "center" }}><CreditCard size={14} /> Square connector</div>
          <span className="sr-badge" style={{ background: cfg?.square?.connected ? "#E7F6EC" : "#EEF1F0", color: cfg?.square?.connected ? "#067647" : "#5B6B85" }}>{cfg?.square?.connected ? "Connected" : cfg?.square?.app_configured ? "Ready" : "Not configured"}</span>
        </div>
        <p className="sr-muted" style={{ fontSize: 13, marginTop: 8 }}>{cfg?.square?.connected ? `Connected to Square (${cfg.square.env}). Orders auto-match to coupons by code.` : (cfg?.square?.note || "Connect Square — order/payment webhooks auto-match real revenue to each coupon.")}</p>
        {cfg?.square?.connected
          ? <button className="sr-btn sr-btn-ghost sr-btn-sm" style={{ marginTop: 10 }} disabled={busy === "sq"} onClick={disconnectSquare} data-testid="square-disconnect-btn"><Trash2 size={13} /> Disconnect Square</button>
          : <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 10 }} disabled={busy === "sq" || !cfg?.square?.app_configured} onClick={connectSquare} data-testid="square-connect-btn">{busy === "sq" ? <Loader2 size={14} className="sr-spin" /> : <><CreditCard size={13} /> Connect Square</>}</button>}
        {cfg?.square?.connected && (
          <button className="sr-btn sr-btn-ghost sr-btn-sm" style={{ marginTop: 10, marginLeft: 8 }} disabled={busy === "sqwh"} onClick={provisionSquareWebhook} data-testid="square-provision-webhook-btn">{busy === "sqwh" ? <Loader2 size={14} className="sr-spin" /> : <><Zap size={13} /> Provision webhook</>}</button>
        )}
        {cfg?.square?.connected && <p className="sr-muted" style={{ fontSize: 11, marginTop: 8 }}>Provision the webhook once your deployed domain is live — it creates the Square subscription and turns on live revenue matchback automatically.</p>}
      </div>

      <div className="sr-card" style={{ marginTop: 16 }} data-testid="matchback-clover-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div className="sr-eyebrow" style={{ display: "flex", gap: 6, alignItems: "center" }}><CreditCard size={14} /> Clover connector</div>
          <span className="sr-badge" style={{ background: clover?.connected ? "#E7F6EC" : "#EEF1F0", color: clover?.connected ? "#067647" : "#5B6B85" }}>
            {clover?.connected ? "Connected" : clover?.app_configured ? "Ready" : "Not configured"}
          </span>
        </div>
        <p className="sr-muted" style={{ fontSize: 13, marginTop: 8 }}>
          {clover?.connected
            ? `Connected to Clover merchant ${clover.merchant_id}. Orders auto-match to coupons by code.`
            : "Connect your Clover POS — we listen for order/payment webhooks and match real revenue to each coupon automatically."}
        </p>
        {clover?.connected ? (
          <button className="sr-btn sr-btn-ghost sr-btn-sm" style={{ marginTop: 10 }} disabled={busy === "cv"} onClick={disconnectClover} data-testid="clover-disconnect-btn"><Trash2 size={13} /> Disconnect Clover</button>
        ) : (
          <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 10 }} disabled={busy === "cv" || !clover?.app_configured} onClick={connectClover} data-testid="clover-connect-btn">
            {busy === "cv" ? <Loader2 size={14} className="sr-spin" /> : <><CreditCard size={13} /> Connect Clover</>}
          </button>
        )}
      </div>

      {summary && (summary.by_offer || []).length > 0 && (
        <div className="sr-card" style={{ marginTop: 16 }} data-testid="matchback-table">
          <div className="sr-eyebrow" style={{ marginBottom: 8 }}>Revenue by offer</div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead><tr style={{ textAlign: "left", color: "var(--muted)" }}><th style={{ padding: 8 }}>Offer</th><th>Orders</th><th>Revenue</th><th>From sends</th><th>Est. cost</th><th>Net</th><th>ROI</th><th>Refunds</th></tr></thead>
            <tbody>{summary.by_offer.map((r) => (
              <tr key={r.offer_id} style={{ borderTop: "1px solid var(--border)" }}>
                <td style={{ padding: 8 }}>{r.name}</td><td>{r.orders}</td><td style={{ color: "#067647", fontWeight: 700 }}>${r.revenue_matched}</td>
                <td title="Revenue attributed to auto-sent coupons">{r.revenue_from_send ? `$${r.revenue_from_send}` : "—"}</td>
                <td>${r.estimated_cost}</td><td>${r.net}</td><td>{r.roi != null ? `${r.roi}x` : "—"}</td>
                <td>{r.refunded_orders ? <span className="sr-badge" style={{ background: "#FCF1DC", color: "#9A7412" }}>{r.refunded_orders} · ${r.refunded_amount}</span> : <span className="sr-muted">—</span>}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------- Ops pages ----------------
const OpsShell = ({ title, children }) => {
  const nav = useNavigate();
  return (
    <div className="sr-root"><div className="sr-app"><div className="sr-main"><div className="sr-content" style={{ padding: 24 }} data-testid="ops-offer-page">
      <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => nav("/ops")} style={{ marginBottom: 12 }}><ArrowLeft size={14} /> Operations</button>
      <h1 style={{ fontSize: 24 }}>{title}</h1>
      <div style={{ marginTop: 16 }}>{children}</div>
    </div></div></div></div>
  );
};

export function OpsWalletIssuance() {
  const [d, setD] = useState(null);
  useEffect(() => { sr.offerStudio.opsWalletIssuance().then(setD).catch(() => setD({ passes: [] })); }, []);
  return (
    <OpsShell title="Wallet issuance">
      {!d ? <Loader2 className="sr-spin" /> : (
        <>
          <p className="sr-muted" style={{ marginBottom: 12 }}>{d.total} passes issued · {Object.entries(d.by_provider || {}).map(([k, v]) => `${k}: ${v}`).join(" · ") || "none yet"}</p>
          <WalletChips providers={d.providers} />
          <div style={{ display: "grid", gap: 8, marginTop: 14 }} data-testid="issuance-list">
            {(d.passes || []).map((p) => <div key={p.id} className="sr-card" style={{ display: "flex", justifyContent: "space-between" }}><span>{p.provider}</span><span className="sr-muted">{p.object_id}</span></div>)}
          </div>
        </>
      )}
    </OpsShell>
  );
}

export function OpsOfferRedemption() {
  const [d, setD] = useState(null);
  useEffect(() => { sr.offerStudio.opsOfferRedemption().then(setD).catch(() => setD({ redemptions: [] })); }, []);
  return (
    <OpsShell title="Offer redemption">
      {!d ? <Loader2 className="sr-spin" /> : (
        <div style={{ display: "grid", gap: 8 }} data-testid="redemption-list">
          <p className="sr-muted">{d.total} redemptions</p>
          {(d.redemptions || []).map((r) => <div key={r.id} className="sr-card" style={{ display: "flex", justifyContent: "space-between" }}><span>{r.code}</span><span className="sr-badge sr-badge-green">{r.status}</span></div>)}
        </div>
      )}
    </OpsShell>
  );
}

export function OpsOfferReconciliation() {
  const [d, setD] = useState(null);
  useEffect(() => { sr.offerStudio.opsOfferReconciliation().then(setD).catch(() => setD({ reconciliation: [] })); }, []);
  return (
    <OpsShell title="Offer reconciliation">
      {!d ? <Loader2 className="sr-spin" /> : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }} data-testid="reconciliation-table">
          <thead><tr style={{ textAlign: "left", color: "var(--muted)" }}><th style={{ padding: 8 }}>Offer</th><th>Redeemed</th><th>Verified</th><th>Est. liability</th></tr></thead>
          <tbody>{(d.reconciliation || []).map((r) => (
            <tr key={r.offer_id} style={{ borderTop: "1px solid var(--border)" }}><td style={{ padding: 8 }}>{r.name}</td><td>{r.redeemed}</td><td>{r.verified_purchases}</td><td>${r.estimated_liability}</td></tr>
          ))}</tbody>
        </table>
      )}
    </OpsShell>
  );
}
